import { ContentCopy } from '@mui/icons-material';
import { Box, Button, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { useNotify, useRecordContext, useTranslate } from 'react-admin';

import { fontFamilyMonospace } from 'layout/theme';
import { LiveLogEntry, LiveLogFieldValue } from 'types/LiveLogs';
import { LIVE_LOGS_I18N } from './liveLogs';

const EMPTY_STRING = '""';

const FieldValue = ({ value }: { value: LiveLogFieldValue }) => {
  if (value === null) {
    return <Box component="em">null</Box>;
  }
  if (value === '') {
    return <>{EMPTY_STRING}</>;
  }
  if (typeof value === 'object') {
    return (
      <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }
  return <>{String(value)}</>;
};

/** Every key of the entry's `fields`, sorted so dot prefixes group. Nothing is truncated. */
export const LogFieldsPanel = () => {
  const record = useRecordContext<LiveLogEntry>();
  const translate = useTranslate();
  const notify = useNotify();

  if (!record) {
    return null;
  }

  const fields = record.fields ?? {};
  const keys = Object.keys(fields).sort((a, b) => a.localeCompare(b));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(fields, null, 2));
      notify(`${LIVE_LOGS_I18N}.expand.copied`, { type: 'info' });
    } catch {
      notify(`${LIVE_LOGS_I18N}.expand.copy_failed`, { type: 'error' });
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<ContentCopy />} onClick={copy}>
          {translate('custom.actions.copy')}
        </Button>
      </Box>
      <Table size="small" aria-label={translate(`${LIVE_LOGS_I18N}.expand.label`)}>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key}>
              <TableCell
                component="th"
                scope="row"
                sx={{ fontFamily: fontFamilyMonospace, fontWeight: 600, verticalAlign: 'top', wordBreak: 'break-word' }}
              >
                {key}
              </TableCell>
              <TableCell sx={{ fontFamily: fontFamilyMonospace, verticalAlign: 'top', wordBreak: 'break-word' }}>
                <FieldValue value={fields[key]} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
